import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Pill, AlertTriangle, ShieldAlert, Plus, ChevronDown, ChevronUp, Microscope, FlaskConical, GitBranch, CircleDot, TestTube2, FileText } from "lucide-react";
import { checkInteractions } from "../lib/drugInteractions";
// PROV: Phase-1 observational provenance (fail-open)
import { provLog } from "../lib/provenance";
import { genId as provGenId, recId as provRecId, medEventFields } from "../lib/provenanceCore";

// Condition ids that must trigger auto-screening for concurrent STIs.
// Kept in this file (not the dataset) because it's UI-workflow logic, not data.
const STI_COINFECTION_CONDITIONS = new Set([
  "gyn-7",  // Trichomoniasis
  "gyn-8",  // PID
  "gyn-25", // Fitz-Hugh-Curtis
]);
const STI_SCREEN_PANEL = [
  "HIV I/II Ab (ELISA)",
  "VDRL / RPR",
  "HBsAg",
  "Anti-HCV",
  "Chlamydia trachomatis NAAT",
  "Neisseria gonorrhoeae NAAT",
];

// Renders when the Secondary Diagnosis matches a known acute condition.
// Mirrors the chronic Push-engine card:
//   - Red-flag amber banner
//   - Cautions strip (dengue "avoid NSAIDs" etc.)
//   - Per-medicine "Prescribe this" pink button
//   - "Prescribe all" bulk button
//   - Inline DDI warning against the current medicines list
//   - STI co-infection auto-screen (for high-risk gynec conditions)
export default function AcuteSuggestionPanel({ condition, currentMedicines = [], patientFlags = {}, onAddMolecule, onAppendInvestigation, onAppendAdvice }) {
  const [collapsed, setCollapsed] = useState(false);

  const already = useMemo(() => {
    const norms = (currentMedicines || [])
      .filter((m) => m?.brand_name || m?.generic_name || m?.composition)
      .map((m) => `${m.brand_name || ""} ${m.generic_name || ""} ${m.composition || ""}`.toLowerCase());
    return (gen) => {
      const g = (gen || "").toLowerCase();
      if (!g) return false;
      const first = g.split(/[+\s(]/)[0];
      return norms.some((n) => n.includes(first) && first.length > 2);
    };
  }, [currentMedicines]);

  // PROV: record acute-protocol candidates actually shown (observational only)
  const provSessRef = useRef(null);
  useEffect(() => {
    if (!condition?.id) return;
    provSessRef.current = provGenId();
    try {
      const cands = (condition.meds || []).map((med, i) => ({
        recommendation_id: provRecId(provSessRef.current, "acute", condition.id, i, med.generic_name),
        section: "acute", rule_key: condition.id, molecule: med.generic_name || "", brand: med.brand_name || "",
        dose: med.strength || "", frequency: med.frequency || "", duration: med.duration || "",
      }));
      if (cands.length) provLog({ event_type: "CG_CANDIDATES", guidance_session_id: provSessRef.current, candidates: cands, count: cands.length, mechanism: "DETERMINISTIC", origin_detail: "acute_protocol" });
    } catch (e) { console.debug("[prov-fail-open]", e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition?.id]);

  if (!condition) return null;

  const addMed = (med, { silent = false } = {}) => {
    const asRow = {
      brand_name: med.brand_name,
      generic_name: med.generic_name,
      composition: med.composition || med.generic_name,
      strength: med.strength || "",
      dose: med.strength || "",
      route: med.route || "Oral",
      dosage_form: med.dosage_form || "Tablet",
      frequency: med.frequency || "",
      timing: "",
      duration: med.duration || "",
      quantity: "",
      instructions: med.instructions || "",
      type: "acute",
      // PROV: origin CLINICAL_GUIDANCE per ruling D1 — physician approval recorded separately as disposition
      _prov: { origin: "CLINICAL_GUIDANCE", detail: "acute_protocol", recommendation_id: provRecId(provSessRef.current || "na", "acute", condition.id, (condition.meds || []).indexOf(med), med.generic_name), guidance_session_id: provSessRef.current },
    };
    provLog({ event_type: "CG_CANDIDATE_ACCEPTED", guidance_session_id: provSessRef.current, recommendation_id: asRow._prov.recommendation_id, physician_action: "ACCEPTED", medicine: medEventFields(asRow), origin_detail: "acute_protocol" }); // PROV
    if (!silent) {
      // Simulate DDI check with this new med added.
      const next = [...(currentMedicines || []).filter((m) => m?.brand_name), asRow];
      const hits = checkInteractions(next, patientFlags).filter((h) => h.severity === "critical" || h.severity === "major");
      if (hits.length > 0) {
        const label = hits[0].label || "drug-drug interaction";
        toast.warning(`Added — but check DDI: ${label}`, { duration: 4500 });
      } else {
        toast.success(`Added "${med.brand_name}" for ${condition.label}`);
      }
    }
    onAddMolecule?.(asRow);
  };

  const remainingCount = (condition.meds || []).filter((m) => !already(m.generic_name)).length;

  const addAll = () => {
    let count = 0;
    (condition.meds || []).forEach((med) => {
      if (already(med.generic_name)) return; // don't duplicate
      addMed(med, { silent: true });
      count += 1;
    });
    if (count === 0) toast.info("All suggested medicines already on the Rx");
    else toast.success(`Added ${count} medicine${count > 1 ? "s" : ""} for ${condition.label}`);
  };

  return (
    <div
      className="mt-3 border border-pink-200 rounded-md bg-gradient-to-br from-pink-50/60 to-white"
      data-testid="acute-rx-panel"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-pink-100">
        <div className="flex items-center gap-2">
          <Pill size={14} className="text-pink-600" />
          <span className="text-xs font-semibold text-pink-800 uppercase tracking-wider">
            Acute Rx Suggestion
          </span>
          <span className="text-[11px] text-slate-600">— {condition.label}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white border border-slate-200 text-slate-500">
            {condition.group}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {onAppendAdvice && (
            <button
              onClick={() => {
                const parts = [`• [${condition.group || "Acute"}] ${condition.label}`];
                if (condition.pathognomonic) parts.push(`— ${condition.pathognomonic}`);
                if (condition.definitiveTreatment) parts.push(`; Plan: ${condition.definitiveTreatment}`);
                if (condition.redFlags?.length) parts.push(`; RED FLAGS: ${condition.redFlags.slice(0, 2).join("; ")}`);
                onAppendAdvice(parts.join(" "));
                // toast handled by parent
              }}
              className="text-[10px] font-semibold uppercase tracking-wider px-2 h-7 rounded border border-emerald-500 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1"
              data-testid="acute-rxnote-header"
              title="Copy this diagnosis's clinical reasoning + treatment plan into the Advice / Rx notes"
            >
              <FileText size={11} /> Rx-note
            </button>
          )}
          <Button
            size="sm"
            className={`h-7 text-xs ${remainingCount === 0 ? "bg-slate-300 hover:bg-slate-300 text-slate-600" : "bg-pink-600 hover:bg-pink-700 text-white"}`}
            onClick={addAll}
            disabled={remainingCount === 0}
            data-testid="acute-prescribe-all"
            title={remainingCount === 0 ? "All suggested meds already on Rx" : "Add every remaining suggested medicine at once"}
          >
            <Plus size={12} className="mr-1" />
            {remainingCount === 0
              ? `All on Rx (${(condition.meds || []).length})`
              : `Prescribe all (${remainingCount})`}
          </Button>
          <button
            className="text-slate-400 hover:text-slate-700"
            onClick={() => setCollapsed((c) => !c)}
            data-testid="acute-collapse-toggle"
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="p-3 space-y-3">
          {/* Pathognomonic — the single most helpful clinical clue */}
          {condition.pathognomonic && (
            <div
              className="border border-sky-300 bg-sky-50 rounded p-2 flex items-start gap-2"
              data-testid="acute-pathognomonic"
            >
              <CircleDot size={13} className="text-sky-700 mt-0.5 shrink-0" />
              <div className="text-[11px] text-sky-900">
                <div className="font-semibold uppercase tracking-wider text-[9px] mb-0.5">Pathognomonic</div>
                <div>{condition.pathognomonic}</div>
              </div>
            </div>
          )}

          {/* Diagnostic tests */}
          {(condition.definitiveTest || condition.supportiveTests?.length > 0) && (
            <div
              className="border border-slate-200 bg-slate-50 rounded p-2"
              data-testid="acute-tests"
            >
              <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center gap-1">
                <FlaskConical size={11} /> Diagnostic work-up
              </div>
              {condition.definitiveTest && (
                <div className="text-[11px] text-slate-800">
                  <span className="text-[9px] uppercase font-bold text-emerald-700 mr-1">Definitive</span>
                  {condition.definitiveTest}
                </div>
              )}
              {condition.supportiveTests?.length > 0 && (
                <div className="text-[11px] text-slate-700 mt-0.5">
                  <span className="text-[9px] uppercase font-bold text-slate-500 mr-1">Supportive</span>
                  {condition.supportiveTests.join(" · ")}
                </div>
              )}
              {STI_COINFECTION_CONDITIONS.has(condition.id) && onAppendInvestigation && (
                <div className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between gap-2" data-testid="sti-coinfection-strip">
                  <div className="flex items-start gap-1.5 text-[10.5px] text-rose-800 flex-1">
                    <TestTube2 size={12} className="text-rose-600 mt-0.5 shrink-0" />
                    <div>
                      <span className="font-semibold">STI co-infection screening recommended:</span>{" "}
                      <span className="text-rose-700">{STI_SCREEN_PANEL.join(" · ")}</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-rose-300 text-rose-700 hover:bg-rose-50 text-[10px] h-6 px-2 shrink-0"
                    onClick={() => {
                      STI_SCREEN_PANEL.forEach((lab) => onAppendInvestigation(lab));
                      toast.success("STI panel added to investigations");
                    }}
                    data-testid="sti-add-panel-btn"
                  >
                    <Plus size={11} className="mr-0.5" /> Add STI Panel
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Differentials — chip strip */}
          {condition.ddx?.length > 0 && (
            <div data-testid="acute-ddx">
              <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center gap-1">
                <GitBranch size={11} /> Differentials
              </div>
              <div className="flex gap-1 flex-wrap">
                {condition.ddx.map((d, i) => (
                  <span key={`ddx-${i}`} className="text-[10px] px-1.5 py-0.5 rounded-full bg-white border border-slate-200 text-slate-700">
                    {d}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Positive / negative signs (collapsible) */}
          {(condition.positiveSigns?.length > 0 || condition.negativeSigns?.length > 0) && (
            <details className="text-[11px]" data-testid="acute-signs">
              <summary className="cursor-pointer text-[9px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                <Microscope size={11} /> Clinical signs
              </summary>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {condition.positiveSigns?.length > 0 && (
                  <div>
                    <div className="text-emerald-700 font-semibold mb-0.5">Positive</div>
                    <ul className="list-disc list-inside space-y-0.5 text-slate-700">
                      {condition.positiveSigns.map((s, i) => <li key={`ps-${i}`}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {condition.negativeSigns?.length > 0 && (
                  <div>
                    <div className="text-rose-700 font-semibold mb-0.5">Negative</div>
                    <ul className="list-disc list-inside space-y-0.5 text-slate-700">
                      {condition.negativeSigns.map((s, i) => <li key={`ns-${i}`}>{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            </details>
          )}

          {condition.redFlags?.length > 0 && (
            <div
              className="border border-amber-300 bg-amber-50 rounded p-2 flex items-start gap-2"
              data-testid="acute-redflags"
            >
              <AlertTriangle size={13} className="text-amber-700 mt-0.5 shrink-0" />
              <div className="text-[11px] text-amber-900">
                <div className="font-semibold mb-0.5">Red flags — escalate / admit:</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {condition.redFlags.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            </div>
          )}

          {condition.cautions?.length > 0 && (
            <div
              className="border border-red-300 bg-red-50 rounded p-2 flex items-start gap-2"
              data-testid="acute-cautions"
            >
              <ShieldAlert size={13} className="text-red-700 mt-0.5 shrink-0" />
              <div className="text-[11px] text-red-900">
                <div className="font-semibold mb-0.5">Cautions:</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {condition.cautions.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            </div>
          )}

          <div className="grid gap-2">
            {(condition.meds || []).map((med, i) => {
              const dup = already(med.generic_name);
              return (
                <div
                  key={i}
                  className="border border-slate-200 rounded p-2 bg-white flex items-start justify-between gap-3"
                  data-testid={`acute-med-${i}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">
                      {med.brand_name}
                      <span className="ml-1 text-[11px] text-slate-500 font-normal">
                        ({med.generic_name})
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-700 mt-0.5">
                      <span className="font-mono">{med.strength}</span>
                      {med.frequency && <span> · {med.frequency}</span>}
                      {med.duration && <span> · {med.duration}</span>}
                      {med.route && med.route !== "Oral" && <span> · {med.route}</span>}
                    </div>
                    {med.instructions && (
                      <div className="text-[11px] text-slate-500 mt-1 italic">
                        {med.instructions}
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    className={`h-7 shrink-0 text-xs ${dup ? "bg-slate-300 hover:bg-slate-300 text-slate-600" : "bg-pink-600 hover:bg-pink-700 text-white"}`}
                    onClick={() => addMed(med)}
                    disabled={dup}
                    data-testid={`acute-prescribe-${i}`}
                    title={dup ? "Already on the Rx" : "Add this medicine to the prescription"}
                  >
                    {dup ? "On Rx" : (<><Plus size={12} className="mr-1" /> Prescribe this</>)}
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="text-[10px] text-slate-500 italic pt-1 border-t border-slate-100">
            Suggestions are decision-support only. Final Rx choice remains with the treating physician.
          </div>
        </div>
      )}
    </div>
  );
}
